/**
 * api.ts – Zentraler API-Wrapper für alle Backend-Aufrufe an actions.php.
 *
 * Ersetzt direkte fetch()-Calls mit einheitlichem Verhalten:
 *   - credentials: 'include' (Session-Cookie mitsenden)
 *   - 401 → automatischer Logout via registrierte Callback-Funktion
 *   - !res.ok → throw ApiError (kein stilles Verschlucken)
 *   - Typisierung via Generics
 *
 * Zusammenhänge:
 *   - AuthContext.tsx  → registriert logout() via registerLogout() beim Mount
 *   - actions.php     → Backend-API-Endpunkt (alle action-Parameter)
 *   - Alle Contexts, Pages, Services → Konsumenten
 *
 * Nicht für andere Endpunkte (notifier.php, configloader.php) gedacht –
 * diese haben eigene Services mit spezifischer Logik.
 *
 * ERWEITERBARKEIT: Neuen API-Call hinzufügen?
 *   1. import { api, apiPost } from '@/services/api';
 *   2. GET:  const data = await api<MyType>('action_name', { param: 'value' });
 *   3. POST: const data = await apiPost<MyType>('action_save', { id: 42, name: 'Test' });
 *   4. Fertig – credentials, 401-Handling und Error-Throwing passieren automatisch.
 *
 * Migration: Abgeschlossen. Verbleibende direkte fetch()-Calls:
 *   - useLazyJsonData.ts (dynamische URL-Abstraktion)
 *   - FileUploadPopup.tsx, FileManagerView.tsx, FileDetailBasedata.tsx (FormData/Binär-Uploads)
 *   - SignuperDetailBasedata.tsx (Bearer-Token Custom-Auth-Header)
 */

const BASE = 'data/actions.php';

/**
 * Registrierte Logout-Funktion für automatisches 401-Handling.
 * Wird vom AuthProvider einmalig gesetzt (registerLogout).
 */
let logoutFn: (() => void) | null = null;

/**
 * Registriert die Logout-Funktion für automatisches 401-Handling.
 * Wird einmalig vom AuthProvider aufgerufen, damit api()/apiPost()
 * bei 401 den User abmelden können, ohne selbst im React-Baum zu stehen.
 */
export function registerLogout(fn: () => void): void {
  logoutFn = fn;
}

/**
 * Custom Error für API-Fehler mit HTTP-Status.
 * Ermöglicht differenziertes Error-Handling: catch-Block kann anhand
 * von `status` entscheiden, ob retry sinnvoll ist.
 */
export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Prüft die Response auf 401 (→ Logout) und !res.ok (→ throw ApiError).
 * Gemeinsame Logik für api() und apiPost(), damit das Verhalten
 * bei GET und POST identisch ist.
 */
async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    logoutFn?.();
    throw new ApiError('Nicht angemeldet.', 401);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as Record<string, unknown>;
    throw new ApiError(
      typeof body?.error === 'string' ? body.error : `Anfrage fehlgeschlagen (${res.status}).`,
      res.status,
    );
  }

  return res.json() as Promise<T>;
}

/**
 * GET-Request an actions.php.
 *
 * Baut die URL aus action + optionalen Params, sendet Session-Cookie,
 * prüft 401 (→ Logout) und !res.ok (→ throw ApiError).
 *
 * @param action  Der action-Parameter (z.B. 'clients', 'invoice')
 * @param params  Optionale Query-Parameter als Key-Value-Objekt
 * @returns       Geparste JSON-Response vom Typ T
 * @throws {ApiError} bei HTTP-Fehler (401 löst zusätzlich Logout aus)
 *
 * @example
 * const clients = await api<Client[]>('clients');
 * const detail = await api<InvoiceDetail>('invoice', { id: '42' });
 */
export async function api<T = unknown>(
  action: string,
  params?: Record<string, string | number | boolean>,
): Promise<T> {
  const query = new URLSearchParams({ action });
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      query.set(key, String(value));
    }
  }

  const res = await fetch(`${BASE}?${query}`, { credentials: 'include' });
  return handleResponse<T>(res);
}

/**
 * POST-Request an actions.php.
 *
 * Sendet JSON-Body mit Session-Cookie, prüft 401 (→ Logout)
 * und !res.ok (→ throw ApiError).
 *
 * @param action  Der action-Parameter (z.B. 'client_save', 'invoice_create')
 * @param body    Request-Body als Objekt (wird zu JSON serialisiert)
 * @returns       Geparste JSON-Response vom Typ T
 * @throws {ApiError} bei HTTP-Fehler (401 löst zusätzlich Logout aus)
 *
 * @example
 * const result = await apiPost<{ success: boolean }>('client_save', { id: 42, name: 'Test' });
 */
export async function apiPost<T = unknown>(
  action: string,
  body: Record<string, unknown>,
): Promise<T> {
  const res = await fetch(`${BASE}?action=${encodeURIComponent(action)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  return handleResponse<T>(res);
}
